import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, X, Type, Users } from 'lucide-react';
import { toast } from 'sonner';
import { FIELD_SPORTS } from '@/lib/sportStats';

/**
 * Modal for Pro users to manually add a match to an existing event schedule.
 * Used for rain make-ups, friendlies, or filling in playoff brackets that
 * weren't auto-generated. Supports free-text placeholder team names
 * (e.g. "1st Place", "Winner of Game 5") for bracket rows where the
 * final seed isn't known yet.
 */
export function AddMatchModal({ open, onClose, event, addMatch }) {
  // Each team side has a mode: 'team' (dropdown of real teams) or
  // 'placeholder' (free-text label like "1st Place").
  const [teamAMode, setTeamAMode] = useState('team');
  const [teamBMode, setTeamBMode] = useState('team');
  const [teamAId, setTeamAId] = useState('');
  const [teamBId, setTeamBId] = useState('');
  const [teamAPlaceholder, setTeamAPlaceholder] = useState('');
  const [teamBPlaceholder, setTeamBPlaceholder] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [venue, setVenue] = useState('');
  const [court, setCourt] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;
  const teams = event?.teams || [];

  const reset = () => {
    setTeamAMode('team'); setTeamBMode('team');
    setTeamAId(''); setTeamBId('');
    setTeamAPlaceholder(''); setTeamBPlaceholder('');
    setDate(''); setTime('');
    setVenue(''); setCourt(''); setLabel('');
  };

  const submit = async () => {
    const aFilled = teamAMode === 'team' ? !!teamAId : !!teamAPlaceholder.trim();
    const bFilled = teamBMode === 'team' ? !!teamBId : !!teamBPlaceholder.trim();
    if (!aFilled || !bFilled) {
      toast.error('Pick a team or type a placeholder for both sides.');
      return;
    }
    if (teamAMode === 'team' && teamBMode === 'team' && teamAId === teamBId) {
      toast.error('Teams must be different.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: date || null,
        time: time || null,
        venue: venue || null,
        court: court ? parseInt(court, 10) : 1,
        match_label: label || null,
        stage: 'manual',
      };
      if (teamAMode === 'team') {
        payload.team_a = teamAId;
      } else {
        payload.team_a_placeholder = teamAPlaceholder.trim();
      }
      if (teamBMode === 'team') {
        payload.team_b = teamBId;
      } else {
        payload.team_b_placeholder = teamBPlaceholder.trim();
      }
      await addMatch(event.id, payload);
      toast.success('Match added to schedule.');
      reset();
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Failed to add match.');
    } finally {
      setSaving(false);
    }
  };

  const renderTeamField = (side) => {
    const isA = side === 'a';
    const mode = isA ? teamAMode : teamBMode;
    const setMode = isA ? setTeamAMode : setTeamBMode;
    const teamId = isA ? teamAId : teamBId;
    const setTeamId = isA ? setTeamAId : setTeamBId;
    const placeholder = isA ? teamAPlaceholder : teamBPlaceholder;
    const setPlaceholder = isA ? setTeamAPlaceholder : setTeamBPlaceholder;
    const otherTeamId = isA ? teamBId : teamAId;
    const label = isA ? 'Team A' : 'Team B';
    const testIdBase = `add-match-team-${side}`;

    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label htmlFor={testIdBase} className="text-xs font-medium text-slate-700">{label}</Label>
          <button
            type="button"
            onClick={() => setMode(mode === 'team' ? 'placeholder' : 'team')}
            className="text-[11px] text-[#00247D] hover:underline flex items-center gap-1"
            data-testid={`${testIdBase}-mode-toggle`}
          >
            {mode === 'team' ? (
              <>
                <Type className="w-3 h-3" /> Use placeholder
              </>
            ) : (
              <>
                <Users className="w-3 h-3" /> Pick a team
              </>
            )}
          </button>
        </div>
        {mode === 'team' ? (
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger id={testIdBase} data-testid={testIdBase}>
              <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {teams.map(t => (
                <SelectItem key={t.id} value={t.id} disabled={t.id === otherTeamId}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={testIdBase}
            value={placeholder}
            onChange={e => setPlaceholder(e.target.value)}
            placeholder='e.g. "1st Place", "Winner of Game 5"'
            maxLength={64}
            data-testid={`${testIdBase}-placeholder`}
          />
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-match-modal">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto border-slate-200 shadow-xl">
        <CardHeader className="flex flex-row items-center justify-between pb-2 sticky top-0 bg-white z-10 border-b border-slate-100">
          <CardTitle className="text-xl font-bold text-[#00247D] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Plus className="w-5 h-5" /> ADD MATCH
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="add-match-close">
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <p className="text-xs text-slate-500">
            Manually add a match — for rain make-ups, friendlies, or extra playoff games not generated automatically.
            Use <span className="font-medium text-slate-700">placeholder</span> for bracket rows where the seed isn&apos;t known yet.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderTeamField('a')}
            {renderTeamField('b')}
            <div>
              <Label htmlFor="add-match-date" className="text-xs font-medium text-slate-700">Date <span className="text-slate-400">(optional)</span></Label>
              <Input id="add-match-date" type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="add-match-date" />
            </div>
            <div>
              <Label htmlFor="add-match-time" className="text-xs font-medium text-slate-700">Time <span className="text-slate-400">(optional)</span></Label>
              <Input id="add-match-time" type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="add-match-time" />
            </div>
            <div>
              <Label htmlFor="add-match-venue" className="text-xs font-medium text-slate-700">Venue <span className="text-slate-400">(optional)</span></Label>
              <Input id="add-match-venue" value={venue} onChange={e => setVenue(e.target.value)} placeholder="e.g. Main Court" data-testid="add-match-venue" />
            </div>
            <div>
              <Label htmlFor="add-match-court" className="text-xs font-medium text-slate-700">{FIELD_SPORTS.includes((event?.sport || '').toLowerCase()) ? 'Field' : 'Court'} # <span className="text-slate-400">(optional)</span></Label>
              <Input id="add-match-court" type="number" min="1" value={court} onChange={e => setCourt(e.target.value)} placeholder="1" data-testid="add-match-court" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="add-match-label" className="text-xs font-medium text-slate-700">Match Label <span className="text-slate-400">(optional — e.g. &quot;Make-up Game&quot;, &quot;Friendly&quot;, &quot;3rd Place&quot;)</span></Label>
              <Input id="add-match-label" value={label} onChange={e => setLabel(e.target.value)} maxLength={64} data-testid="add-match-label" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="add-match-submit">
              {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding…</>) : (<><Plus className="w-4 h-4 mr-2" />Add Match</>)}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
